import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import SubPage from '#/common/components/SubPage';
import useFindingQuery from '../hooks/useFindingQuery';
import FindingForm from '../components/FindingForm/FindingForm';
import { Finding } from '#/graphql/types';
import useAssetQuery from '#/modules/asset/hooks/useAssetQuery';
import getFormTitle from '#/common/utils/getFormTitle';
import useAlertQuery from '#/modules/alert/hooks/useAlertQuery';

export interface FindingParams {
  readonly id?: string;
}

const FindingPage: React.FC = () => {
  const { id } = useParams() as FindingParams;
  const [searchParams, setSearchParams] = useSearchParams();
  const assetId = searchParams.get('assetId');
  const tenantIdFromParams = searchParams.get('tenantId');
  const alertId = searchParams.get('alertId') || undefined;

  const { data: findingQueryData, loading: findingQueryLoading } = useFindingQuery(id);
  const finding: Finding = findingQueryData?.getFinding?.data ?? ({} as Finding);

  const { data: assetQueryData, loading: assetQueryLoading } = useAssetQuery(assetId ?? '', {
    skip: !!tenantIdFromParams,
  });
  const tenantIdFromAsset = assetQueryData?.getAsset?.data?.tenantId;

  if (!tenantIdFromParams && tenantIdFromAsset) {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tenantId', tenantIdFromAsset);
    setSearchParams(newSearchParams, { replace: true });
  }

  const { data: alertQueryData, loading: alertQueryLoading } = useAlertQuery(alertId ?? '', {
    skip: !alertId,
  });
  const alert = alertQueryData?.getAlert?.data;

  const title = getFormTitle(id, finding.displayNumber, 'Finding');
  const loading = findingQueryLoading || assetQueryLoading;

  return (
    <SubPage title={title} showGoBackButton type="form" isLoading={loading}>
      <FindingForm
        title={title}
        finding={
          id === 'new' || !id
            ? ({
                assetId,
                tenantId: tenantIdFromParams || tenantIdFromAsset,
              } as Finding)
            : finding
        }
        alert={alert}
      />
    </SubPage>
  );
};

export default React.memo(FindingPage);
